// @ts-nocheck
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  TextField,
  Typography,
  DialogActions,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/lucideIcons/x.svg';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';

import DialogTransition from '../../../Common/Transition/DialogTransition';
import {
  copyToClipboard,
  groupTemplateList,
  getTemplateAttributes,
  isValueEmpty,
} from '../../../../utils/Utils';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';
import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import renderTemplateVersionOption from '../../../Common/TemplateVersionOption';
import SectionAttributePopover from '../../../Common/SectionAttributePopover';
import useTemplateVersion from '../../../../features/configuration/template/hooks/useTemplateVersion';
import Multiselect from '../../../Common/Multiselect';
import CustomPopover from '../../../Common/CustomPopover';

const convertToFormatString = (data) => {
  const { value } = data;

  if (value) {
    if (value?.attribute_origin === 'common') {
      return `{{system.${value?.name ? value?.name : ''}}}`;
    }
    return `{{attributes.${value?.name ? value?.name : ''}}}`;
  }

  return '';
};

const handleIsOptionEqualToValue = (option, val) =>
  option?.value === val?.value;
const handleGetOptionLabel = (option) => option?.label;
const groupBy = (option) => option?.type || '';

const SmartValuesModal = ({
  open,
  handleClose,
  handleOnChange,
  value,
  attributes = EMPTY_ARRAY,
  AttributesOption = EMPTY_OBJECT,
  isConditionalModal = false,
  loadingTemplates = false,
  getCustomEntityTemplate,
  entityList,
  selectedTemplateVersions = EMPTY_ARRAY,
  insertAtCursor = false,
  autoSelect = false,
  showSimpleLabel = false,
  disableTemplateSelection = false,
}) => {
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [templateVersionList, setTemplateVersionList] = useState([]);
  const [templateAttributes, setTemplateAttributes] = useState([]);

  const { getTemplateVersionList } = useTemplateVersion();

  // TODO
  const templateOptions = useMemo(() => {
    if (isConditionalModal) {
      return EMPTY_ARRAY;
    }

    return groupTemplateList(entityList);
  }, [entityList, isConditionalModal]);

  const templateVersionOptions = useMemo(() => {
    // When selected template versions are already provided by parent modals,
    // use them directly as the available options. This prevents the selector
    // from showing unrelated version values fetched from the backend.
    return selectedTemplateVersions?.length > 0
      ? selectedTemplateVersions
      : templateVersionList;
  }, [selectedTemplateVersions, templateVersionList]);

  const smartValuesFormikForm = useFormik({
    initialValues: {
      value: '',
      template: null,
      templateVersion: [],
    },
    validationSchema: null,
    onSubmit: (values) => {
      // Append the selected value into the derived formula
      if (insertAtCursor) {
        handleOnChange(convertToFormatString(values));
      } else {
        handleOnChange(`${value}${convertToFormatString(values)}`);
      }
      // eslint-disable-next-line no-use-before-define
      handleCloseModal();
    },
  });

  const {
    value: selectedAttributeValue,
    template: selectedTemplateValue,
    templateVersion: selectedTemplateVersion,
  } = smartValuesFormikForm.values;

  useEffect(() => {
    if (
      open &&
      autoSelect &&
      !isConditionalModal &&
      templateOptions?.length === 1
    ) {
      smartValuesFormikForm.setFieldValue('template', templateOptions[0]);
    }
  }, [open, isConditionalModal, autoSelect, templateOptions]);

  /**
   * Closes the smart values modal and resets the formik form.
   * @name handleCloseModal
   * @function
   * @returns {void}
   * @example handleCloseModal()
   */
  const handleCloseModal = useCallback(() => {
    handleClose();
    smartValuesFormikForm.resetForm();
    setTemplateVersionList([]);
    setTemplateAttributes([]);
    setLoadingAttributes(false);
  }, [handleClose, smartValuesFormikForm]);

  /**
   * Handles template selection change
   */
  const handleChangeTemplate = useCallback(
    (_, newValue) => {
      smartValuesFormikForm?.setValues((prev) => ({
        ...prev,
        template: newValue,
        templateVersion: null,
        value: '',
      }));
      setTemplateVersionList([]);
      setTemplateAttributes([]);
    },
    [smartValuesFormikForm, setTemplateVersionList, setTemplateAttributes]
  );

  const handleChangeTemplateVersion = useCallback(
    (val) => {
      let nextValue = [];
      if (Array.isArray(val)) {
        nextValue = val;
      } else if (!isValueEmpty(val)) {
        nextValue = [val];
      }

      smartValuesFormikForm?.setValues((prev) => ({
        ...prev,
        templateVersion: nextValue,
        value: '',
      }));
      setTemplateAttributes([]);
    },
    [smartValuesFormikForm, setTemplateAttributes]
  );

  /**
   * Copies the selected smart value to the clipboard.
   * @name handleCopyText
   * @function
   * @returns {void}
   * @example handleCopyText()
   */
  const handleCopyText = useCallback(
    () => copyToClipboard(convertToFormatString(smartValuesFormikForm?.values)),
    [convertToFormatString, smartValuesFormikForm?.values, copyToClipboard]
  );

  /**
   * Handles the change of the selected field and sets the selected field value in the formik form.
   * @name handleChangeField
   * @function
   * @param {React.ChangeEvent<HTMLInputElement>} event - The input change event.
   * @param {string} newValue - The new selected value.
   * @returns {void}
   * @example handleChangeField(event, newValue)
   */
  const handleChangeField = useCallback(
    (_, newValue) => smartValuesFormikForm?.setFieldValue('value', newValue),
    [smartValuesFormikForm]
  );

  /**
   * Renders the template input field.
   * @name handleRenderInputTemplate
   * @function
   * @param {React.ChangeEvent<HTMLInputElement>} event - The input change event.
   * @returns {React.ReactNode} The rendered input field.
   * @example handleRenderInputTemplate(event)
   */
  const handleRenderInputTemplate = useCallback(
    (params) => (
      <TextField
        {...params}
        label="Template"
        placeholder="All templates"
        slotProps={{
          ...params?.slotProps,
          input: {
            ...params.slotProps?.input,
            endAdornment: (
              <>
                {loadingTemplates ? (
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                ) : null}
                {params?.slotProps?.input?.endAdornment}
              </>
            ),
          },

          inputLabel: { ...params?.slotProps?.inputLabel, shrink: true },
        }}
      />
    ),
    [loadingTemplates]
  );

  const handleRenderInputTemplateVersionType = useCallback(
    (params) => (
      <TextField
        {...params}
        label="Template Version"
        placeholder={
          selectedTemplateVersion?.length ? '' : 'Select template version'
        }
        spellCheck={true}
        slotProps={{
          ...params?.slotProps,
          inputLabel: { ...params?.slotProps?.inputLabel, shrink: true },
        }}
      />
    ),
    [selectedTemplateVersion]
  );

  /**
   * Renders the input field.
   * @name handleRenderInputField
   * @function
   * @param {React.ChangeEvent<HTMLInputElement>} event - The input change event.
   * @returns {React.ReactNode} The rendered input field.
   * @example handleRenderInputField(event)
   */
  const handleRenderInputField = useCallback(
    (params) => (
      <TextField
        {...params}
        label="Field"
        placeholder="Select field"
        spellCheck={true}
        slotProps={{
          ...params?.slotProps,
          input: {
            ...params.slotProps?.input,
            endAdornment: (
              <>
                {loadingAttributes ? (
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                ) : null}
                {params?.slotProps?.input?.endAdornment}
              </>
            ),
          },

          inputLabel: { ...params?.slotProps?.inputLabel, shrink: true },
        }}
      />
    ),
    [loadingAttributes]
  );

  const handleAddTextTitle = useMemo(() => {
    if (!smartValuesFormikForm?.values?.value)
      return 'Please select a value to add';
    return 'Add';
  }, [smartValuesFormikForm?.values?.value]);

  const handleCopyTextTitle = useMemo(() => {
    if (!smartValuesFormikForm?.values?.value)
      return 'Please select a value to copy';
    return 'Copy';
  }, [smartValuesFormikForm?.values?.value]);

  const attributesOptions = useMemo(() => {
    if (isConditionalModal) {
      return AttributesOption;
    }

    if (templateAttributes?.length > 0) {
      return templateAttributes;
    }

    if (!selectedTemplateValue) {
      return attributes;
    }
    return [];
  }, [
    isConditionalModal,
    templateAttributes,
    attributes,
    AttributesOption,
    selectedTemplateValue,
  ]);

  const handleGetTemplateVersionList = useCallback(
    (template) => {
      getTemplateVersionList({ template_id: template?.id }).then((res) => {
        if (res?.data) {
          const updatedList = (res?.data?.versions ?? []).map((item) => ({
            ...item,
            value: item.id,
            label: item.version_number,
          }));

          const defaultVersion =
            updatedList?.find((item) => item?.is_default) ||
            updatedList?.[0] ||
            null;

          setTemplateVersionList(updatedList);

          const prefillVersions = (selectedTemplateVersions || [])
            ?.filter((selected) =>
              updatedList?.some((version) => version?.value === selected?.value)
            )
            ?.map((selected) =>
              updatedList?.find((version) => version?.value === selected?.value)
            )
            ?.filter(Boolean);

          // If there are preselected template versions that match the fetched
          // version list, use them as the initial selection. Otherwise, fall back
          // to the default version from the backend (or the first version if no
          // explicit default exists).
          let initialTemplateVersions = [];

          if (prefillVersions?.length > 0) {
            initialTemplateVersions = prefillVersions;
          } else if (defaultVersion) {
            initialTemplateVersions = [defaultVersion];
          }

          smartValuesFormikForm?.setFieldValue(
            'templateVersion',
            initialTemplateVersions
          );
        }
      });
    },
    [getTemplateVersionList, smartValuesFormikForm, selectedTemplateVersions]
  );

  const handleRenderFieldOptions = useCallback(
    ({ sx, ...otherProps }, option) => (
      <Box
        {...otherProps}
        key={option?.value}
        title={`${option?.label}`}
        sx={[
          {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 4,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <Typography
          noWrap
          sx={{
            mb: 0.2,
            width: 300,
          }}
        >
          {showSimpleLabel ? option?.label : `${option?.id} : ${option?.label}`}
        </Typography>
      </Box>
    ),
    [showSimpleLabel]
  );

  useEffect(() => {
    if (!isValueEmpty(selectedTemplateValue)) {
      handleGetTemplateVersionList(selectedTemplateValue);
      return;
    }
  }, [selectedTemplateValue]);

  useEffect(() => {
    if (!selectedTemplateValue || !selectedTemplateVersion?.length) {
      return;
    }

    setLoadingAttributes(true);
    let versionIds = [];

    // selectedTemplateVersion can be either an array when multiple versions
    // are selected or a single object in legacy/edge cases. Normalize it to an
    // array of version IDs for the backend payload.
    if (Array.isArray(selectedTemplateVersion)) {
      versionIds = selectedTemplateVersion
        ?.map((version) => version?.id)
        ?.filter(Boolean);
    } else if (selectedTemplateVersion?.id) {
      versionIds = [selectedTemplateVersion?.id];
    }

    const fetchParams = {
      template_id: selectedTemplateValue?.id,
      template_version_ids: versionIds,
      version_ids: versionIds?.join(','),
    };

    getCustomEntityTemplate(fetchParams, false)
      .then((res) => {
        if (res?.data) {
          setTemplateAttributes(getTemplateAttributes(res?.data) || []);
        }
      })
      .finally(() => {
        setLoadingAttributes(false);
      });
  }, [selectedTemplateValue, selectedTemplateVersion]);

  return (
    <Dialog
      disableBackdropClick
      onClose={handleCloseModal}
      open={open}
      maxWidth="sm"
      fullWidth={true}
      aria-labelledby="edit-if-node-modal"
      slots={{
        transition: DialogTransition,
      }}
    >
      <DialogTitle>
        <Box component="span" aria-live="polite" aria-atomic="true">
          Smart Values
        </Box>
      </DialogTitle>
      <IconButton
        color="primary"
        aria-label="Close dialog"
        onClick={handleCloseModal}
        sx={{
          position: 'absolute',
          right: 16,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Grid container>
          <Grid container spacing={2} size={12}>
            {!isConditionalModal && (
              <>
                <Grid size={12}>
                  <Autocomplete
                    {...smartValuesFormikForm.getFieldProps('template')}
                    fullWidth
                    options={templateOptions}
                    value={selectedTemplateValue || null}
                    onChange={handleChangeTemplate}
                    popupIcon={<DropdownArrow />}
                    getOptionLabel={handleGetOptionLabel}
                    isOptionEqualToValue={handleIsOptionEqualToValue}
                    groupBy={groupBy}
                    disableClearable={['', undefined].includes(
                      selectedTemplateValue
                    )}
                    renderInput={handleRenderInputTemplate}
                    loading={loadingTemplates}
                    disabled={loadingTemplates || disableTemplateSelection}
                  />
                </Grid>
                <Grid size={12}>
                  <Multiselect
                    id="templateVersion"
                    options={templateVersionOptions || EMPTY_ARRAY}
                    onChange={handleChangeTemplateVersion}
                    value={selectedTemplateVersion || []}
                    disableClearable={isValueEmpty(selectedTemplateVersion)}
                    renderInput={handleRenderInputTemplateVersionType}
                    renderOption={renderTemplateVersionOption}
                    slots={{
                      popper: CustomPopover,
                    }}
                    limitTags={1}
                    getOptionLabel={handleGetOptionLabel}
                    isOptionEqualToValue={handleIsOptionEqualToValue}
                    disabled={
                      loadingTemplates ||
                      disableTemplateSelection ||
                      !selectedTemplateValue
                    }
                  />
                </Grid>
              </>
            )}
            <Grid size={12}>
              <SectionAttributePopover
                options={attributesOptions}
                value={selectedAttributeValue || null}
                onChange={handleChangeField}
                getOptionLabel={handleGetOptionLabel}
                renderInput={handleRenderInputField}
                renderOption={handleRenderFieldOptions}
                loading={loadingAttributes}
                enableSectionFilter={isConditionalModal}
              />
            </Grid>
            <Grid
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
              size={12}
            >
              <Typography variant="h4">
                {convertToFormatString(smartValuesFormikForm?.values)}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseModal} variant="outlined" title="Cancel">
          Cancel
        </Button>
        <Button
          onClick={smartValuesFormikForm?.handleSubmit}
          disabled={!selectedAttributeValue}
          variant="contained"
          title={handleAddTextTitle}
        >
          Add
        </Button>
        <Button
          onClick={handleCopyText}
          title={handleCopyTextTitle}
          disabled={!selectedAttributeValue}
          variant="contained"
        >
          Copy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SmartValuesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleOnChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  AttributesOption: PropTypes.oneOfType([PropTypes.object]).isRequired,
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  isConditionalModal: PropTypes.bool,
  loadingTemplates: PropTypes.bool,
  getCustomEntityTemplate: PropTypes.func.isRequired,
  entityList: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object]))
    .isRequired,
  selectedTemplateVersions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  insertAtCursor: PropTypes.bool,
  autoSelect: PropTypes.bool,
  showSimpleLabel: PropTypes.bool,
  disableTemplateSelection: PropTypes.bool,
};

export default SmartValuesModal;
